import { createContext, useContext, useEffect, useState } from "react";
import { API_BASE } from "../utils/Instance";

const AuthContext = createContext(null);

function stripSensitiveTokens(profile) {
  if (!profile) return null;
  const { accessToken, refreshToken, ...safeProfile } = profile;
  return safeProfile;
}

function clearStoredSessionData() {
  localStorage.removeItem("se_profile");
  localStorage.removeItem("token");
  localStorage.removeItem("role");
}

function buildSessionProfile(access, userProfile = {}) {
  return {
    ...userProfile,
    screenName: userProfile.screenName || userProfile.screen_name || userProfile.username || userProfile.email || "User",
    accessToken: access,
  };
}

function getStoredProfile() {
  try {
    const raw = localStorage.getItem("se_profile");
    if (raw) return stripSensitiveTokens(JSON.parse(raw));
  } catch {}
  return null;
}

function getCookie(name) {
  if (typeof document === "undefined") return "";

  return document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split("=")[1] || "";
}

async function getCsrfToken() {
  const response = await fetch(`${API_BASE}accounts/csrf/`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to initialize CSRF token");
  }

  const data = await response.json().catch(() => ({}));
  return data.csrfToken || decodeURIComponent(getCookie("csrftoken"));
}

async function postWithCsrf(url) {
  const csrfToken = await getCsrfToken();

  return fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "X-CSRFToken": csrfToken,
    },
  });
}

async function requestAccessTokenFromCookie() {
  const response = await postWithCsrf(`${API_BASE}accounts/token/refresh/`);

  if (!response.ok) return null;

  const data = await response.json().catch(() => ({}));
  return data.access || null;
}

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(getStoredProfile);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const storedProfile = getStoredProfile();

      if (!storedProfile) {
        if (isMounted) setAuthReady(true);
        return;
      }

      try {
        const accessToken = await requestAccessTokenFromCookie();

        if (!isMounted) return;

        if (accessToken) {
          setProfile({ ...storedProfile, accessToken });
        } else {
          setProfile(null);
          clearStoredSessionData();
        }
      } catch (error) {
        console.error("Error restoring session:", error);
        if (isMounted) {
          setProfile(null);
          clearStoredSessionData();
        }
      } finally {
        if (isMounted) setAuthReady(true);
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  // Persist only non-token profile data. Access stays in memory; refresh stays in HttpOnly cookie.
  useEffect(() => {
    try {
      if (profile) localStorage.setItem("se_profile", JSON.stringify(stripSensitiveTokens(profile)));
      else localStorage.removeItem("se_profile");
    } catch {}
  }, [profile]);

  const clearSession = () => {
    setProfile(null);
    clearStoredSessionData();
  };

  const login = (access, _refresh, userProfile) => {
    const nextProfile = buildSessionProfile(access, userProfile);
    clearStoredSessionData();
    setProfile(nextProfile);
    if (nextProfile.role) localStorage.setItem("role", nextProfile.role);
  };

  const logout = async () => {
    clearSession();

    return postWithCsrf(`${API_BASE}accounts/logout/`).catch((error) => {
      console.warn("Logout cookie cleanup failed:", error);
    });
  };

  // Function to refresh the access token
  const refreshAccessToken = async () => {
    try {
      const accessToken = await requestAccessTokenFromCookie();

      if (accessToken) {
        setProfile({
          ...(stripSensitiveTokens(profile) || {}),
          accessToken,
        });
        return accessToken;
      }

      console.error("Failed to refresh token");
      logout();
      return null;
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout();
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ profile, setProfile, login, logout, clearSession, refreshAccessToken, authReady }}>
      {children}
    </AuthContext.Provider>
  );
}


export function useAuth() {
  return useContext(AuthContext);
}
